import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { 
  ArrowLeft, Activity, Plus, Heart, Thermometer, Scale, 
  Droplets, Wind, TrendingUp, TrendingDown, Minus, Loader2, AlertTriangle, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const METRIC_ICONS = {
  blood_pressure: Heart,
  heart_rate: Activity,
  weight: Scale,
  temperature: Thermometer,
  blood_glucose: Droplets,
  oxygen_saturation: Wind
};

const METRIC_COLORS = {
  blood_pressure: 'text-rose-500 bg-rose-100',
  heart_rate: 'text-red-500 bg-red-100',
  weight: 'text-blue-500 bg-blue-100',
  temperature: 'text-orange-500 bg-orange-100',
  blood_glucose: 'text-purple-500 bg-purple-100',
  oxygen_saturation: 'text-teal-500 bg-teal-100'
};

export default function HealthMetrics() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});
  const [metrics, setMetrics] = useState([]);
  const [metricTypes, setMetricTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [selectedType, setSelectedType] = useState('blood_pressure');
  const [formData, setFormData] = useState({});
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesRes, summaryRes, metricsRes] = await Promise.all([
        api.get('/health-metrics/types'),
        api.get('/health-metrics/summary'),
        api.get('/health-metrics?days=30')
      ]);
      setMetricTypes(typesRes.data.metric_types || {});
      setSummary(summaryRes.data.summary || {});
      setMetrics(metricsRes.data.metrics || []);
    } catch (error) {
      console.error('Failed to fetch health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogMetric = async (e) => {
    e.preventDefault();
    try {
      const typeConfig = metricTypes[selectedType];
      const value = {};
      
      typeConfig.fields.forEach(field => {
        if (formData[field]) {
          value[field] = parseFloat(formData[field]);
        }
      });

      await api.post('/health-metrics', {
        metric_type: selectedType,
        value,
        unit: typeConfig.unit,
        notes
      });
      
      toast.success('Reading logged!');
      setShowLog(false);
      setFormData({});
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to log reading');
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <TrendingUp className="w-4 h-4 text-amber-500" />;
    if (trend === 'decreasing') return <TrendingDown className="w-4 h-4 text-blue-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getStatusBadge = (status) => {
    if (status === 'high') return <Badge className="bg-rose-100 text-rose-700">High</Badge>;
    if (status === 'low') return <Badge className="bg-blue-100 text-blue-700">Low</Badge>;
    return <Badge className="bg-green-100 text-green-700">Normal</Badge>;
  };

  const formatValue = (type, value) => {
    if (!value) return '—';
    if (type === 'blood_pressure') {
      return `${value.systolic || '—'}/${value.diastolic || '—'}`;
    }
    return value.value || '—';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="health-metrics-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Health Metrics</h1>
              <p className="text-xs sm:text-sm text-slate-500">Track vital signs and health data</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <FileText className="w-4 h-4" />
              Export Report
            </Button>
            <Dialog open={showLog} onOpenChange={setShowLog}>
              <DialogTrigger asChild>
                <Button data-testid="log-metric-btn">
                  <Plus className="w-4 h-4" />
                  Log Reading
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Log Health Reading</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleLogMetric} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Metric Type</Label>
                    <Select value={selectedType} onValueChange={(v) => { setSelectedType(v); setFormData({}); }}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(metricTypes).map(([key, config]) => (
                          <SelectItem key={key} value={key}>{config.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {metricTypes[selectedType]?.fields.map((field) => (
                    <div key={field} className="space-y-2">
                      <Label className="capitalize">
                        {field === 'value' ? metricTypes[selectedType].label : field}
                        {' '}({metricTypes[selectedType].unit})
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData[field] || ''}
                        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                        placeholder={`Enter ${field}`}
                        required
                        data-testid={`input-${field}`}
                      />
                    </div>
                  ))}

                  <div className="space-y-2">
                    <Label>Notes (optional)</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any observations..."
                      rows={2}
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowLog(false)}>Cancel</Button>
                    <Button type="submit">Log Reading</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(summary).map(([type, data]) => {
            const Icon = METRIC_ICONS[type] || Activity;
            const colorClass = METRIC_COLORS[type] || 'text-slate-500 bg-slate-100';
            
            return (
              <Card key={type} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => {
                setSelectedType(type);
                setShowLog(true);
              }}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    {data.trend && getTrendIcon(data.trend)}
                  </div>
                  <div className="mt-3">
                    <p className="text-sm text-slate-500">{data.label}</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {data.latest ? formatValue(type, data.latest.value) : '—'}
                    </p>
                    <p className="text-xs text-slate-400">{data.unit}</p>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    {data.latest && getStatusBadge(data.status)}
                    <span className="text-xs text-slate-400">{data.readings_count} readings</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Readings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Readings</CardTitle>
          </CardHeader>
          <CardContent>
            {metrics.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No readings logged yet</p>
                <Button className="mt-4" onClick={() => setShowLog(true)}>
                  <Plus className="w-4 h-4" />
                  Log First Reading
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {metrics.slice(0, 20).map((metric) => {
                  const Icon = METRIC_ICONS[metric.metric_type] || Activity;
                  const colorClass = METRIC_COLORS[metric.metric_type] || 'text-slate-500 bg-slate-100';
                  const config = metricTypes[metric.metric_type] || {};
                  
                  return (
                    <div key={metric.metric_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{config.label || metric.metric_type}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(metric.logged_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold">
                          {formatValue(metric.metric_type, metric.value)}
                        </p>
                        <p className="text-xs text-slate-400">{metric.unit}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Log Buttons */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Log</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
              {Object.entries(metricTypes).map(([type, config]) => {
                const Icon = METRIC_ICONS[type] || Activity;
                const colorClass = METRIC_COLORS[type] || 'text-slate-500 bg-slate-100';
                
                return (
                  <Button
                    key={type}
                    variant="outline"
                    className="flex flex-col h-auto py-4"
                    onClick={() => { setSelectedType(type); setShowLog(true); }}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-xs">{config.label}</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
